import { createHebrewPDF, formatDate } from './invoicePdfGenerator';

const MOOD_LABELS = {
    very_distressed: 'קשה מאוד',
    distressed: 'קשה',
    neutral: 'ניטרלי',
    good: 'טוב',
    excellent: 'מצוין',
};

const PAGE_HEIGHT = 297; // A4 mm
const BOTTOM_MARGIN = 20;

/**
 * Generate a journal entries report PDF with Hebrew support.
 * Each entry is rendered as a full card so that no content is truncated.
 *
 * @param {Array} entries - Array of decrypted journal entry objects
 * @param {string|null} customerName - Filtered customer name, or null for all
 * @param {string} businessName - Name of the business
 * @returns {Blob} - PDF blob
 */
export const generateJournalPDF = (entries, customerName, businessName) => {
    const doc = createHebrewPDF();

    const pageWidth = doc.internal.pageSize.width;
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    let yPos = 20;

    // ── Helpers ──────────────────────────────────────────────────────────────

    const ensureSpace = (needed) => {
        if (yPos + needed > PAGE_HEIGHT - BOTTOM_MARGIN) {
            doc.addPage();
            yPos = 20;
        }
    };

    // Render a label-value pair on a single line (label bold-ish, value normal)
    const renderField = (label, value) => {
        if (!value) return;
        const line = `${label}: ${value}`;
        ensureSpace(7);
        doc.setFontSize(9);
        doc.text(line, pageWidth - margin, yPos, { align: 'right' });
        yPos += 6;
    };

    // Render a labelled block of wrapped text
    const renderWrappedField = (label, text) => {
        if (!text || !text.trim()) return;
        doc.setFontSize(9);
        const lines = doc.splitTextToSize(text, contentWidth - 4);
        // label — only needs one line of space
        ensureSpace(7);
        doc.text(`${label}:`, pageWidth - margin, yPos, { align: 'right' });
        yPos += 6;
        // wrapped value, indented slightly — break page per line
        lines.forEach(line => {
            ensureSpace(6);
            doc.text(line, pageWidth - margin - 4, yPos, { align: 'right' });
            yPos += 5.5;
        });
    };

    // ── Document header ───────────────────────────────────────────────────────

    doc.setFontSize(20);
    doc.text(businessName, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    doc.setFontSize(16);
    doc.text('יומן טיפולים', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    if (customerName) {
        doc.setFontSize(12);
        doc.text(`${customerName} :לקוח`, pageWidth / 2, yPos, { align: 'center' });
        yPos += 8;
    }

    // Summary box
    doc.setFillColor(249, 245, 255);
    doc.rect(margin, yPos, contentWidth, 14, 'F');
    doc.setFontSize(11);
    doc.text(
        `סה"כ רשומות: ${entries.length}`,
        pageWidth - margin - 5, yPos + 9,
        { align: 'right' }
    );
    yPos += 22;

    // ── Entry cards ───────────────────────────────────────────────────────────

    entries.forEach((entry, index) => {
        const sd = entry.sessionData || {};

        // Estimate minimum card height to decide whether to push to next page
        ensureSpace(18);

        // Card header bar (purple)
        const headerY = yPos;
        doc.setFillColor(102, 51, 153);
        doc.rect(margin, headerY, contentWidth, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);

        const dateStr = entry.entry_date ? formatDate(entry.entry_date) : '-';
        const custStr = entry.customer?.full_name || '-';
        const sessionStr = entry.session_number
            ? `מפגש ${entry.session_number}`
            : '';

        // Right side: date
        doc.text(dateStr, pageWidth - margin - 3, headerY + 7, { align: 'right' });
        // Center: customer name
        doc.text(custStr, pageWidth / 2, headerY + 7, { align: 'center' });
        // Left side: session number
        if (sessionStr) {
            doc.text(sessionStr, margin + 3, headerY + 7, { align: 'left' });
        }

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
        yPos = headerY + 14;

        // Meta fields row
        const metaParts = [];
        if (sd.duration_minutes) metaParts.push(`משך: ${sd.duration_minutes} דקות`);
        if (sd.mood_assessment)  metaParts.push(`מצב רוח: ${MOOD_LABELS[sd.mood_assessment] || sd.mood_assessment}`);
        if (metaParts.length > 0) {
            ensureSpace(7);
            doc.text(metaParts.join('   |   '), pageWidth - margin, yPos, { align: 'right' });
            yPos += 7;
        }

        // Interventions (joined as comma-separated list)
        const interventionsText = Array.isArray(sd.interventions_used)
            ? sd.interventions_used.filter(i => i?.trim()).join(', ')
            : (sd.interventions_used || '');
        renderWrappedField('התערבויות', interventionsText);

        // Session notes (full, wrapped)
        renderWrappedField('הערות סשן', sd.session_notes);

        // Homework
        renderWrappedField('שיעורי בית', sd.homework_assigned);

        // Separator line between entries (not after last)
        if (index < entries.length - 1) {
            yPos += 3;
            ensureSpace(4);
            doc.setDrawColor(220, 220, 220);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 5;
        }
    });

    return doc.output('blob');
};
